"use client";

import { ReactNode } from "react";
import Image, { getImageProps } from "next/image";
import Link from "next/link";

import Caret from "@/icons/arrows/caret-right.svg";
import Heading from "~/componentsDS/Typography/Heading";
import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { cn } from "~/lib/utils";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

type CTA = {
    label: string;
    href: string;
}

type AnalyticEvent = {
    click_text: string;
    product_name: string;
};

type ProductOverviewCardProps = {
    desktopImageUrl: string;
    mobileImageUrl: string;
    title: ReactNode;
    description: ReactNode;
    logo: ReactNode;
    cta: CTA;
    analyticEvent: AnalyticEvent;
    contentCn?: string;
};

const ProductOverviewCard = ({
    desktopImageUrl,
    mobileImageUrl,
    title,
    description,
    logo,
    cta,
    analyticEvent,
    contentCn,
}: ProductOverviewCardProps) => {
    const {
        props: desktopImageProps,
    } = getImageProps({
        src: desktopImageUrl,
        alt: "",
        fill: true,
        sizes: "(max-width: 1280px) 88vw, 1216px",
    });
    const sendEvent = useTrackEvent();

    const handleClick = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "what_you_need_to_know",
                meta_data: {
                    click_text: analyticEvent.click_text,
                    product_name: analyticEvent.product_name,
                },
            },
        }, cta.href);
    };

    return (
        <Link
            href={cta.href}
            onClick={handleClick}
            className="group/poc relative flex flex-col md:flex-row md:justify-between gap-6 px-6 py-8 md:p-12 w-full h-[469px] md:h-[552px] rounded-3xl overflow-hidden"
        >
            <picture className="absolute inset-0">
                <source
                    media="(min-width: 768px)"
                    srcSet={desktopImageProps.srcSet}
                    sizes={desktopImageProps.sizes}
                />
                <Image
                    src={mobileImageUrl}
                    alt=""
                    fill
                    sizes="88vw"
                    loading="lazy"
                    className="object-cover object-center pointer-events-none"
                />
            </picture>
            <div className={cn(
                "relative max-w-[415px] text-pureWhite",
                contentCn
            )}>
                <Heading as="h3" size="xs" sizes={{md: "sm"}} weight="400" className="mb-3 md:mb-6 md:font-light">{title}</Heading>
                <Paragraph size="md" sizes={{md: "lg"}} className="md:mb-12">{description}</Paragraph>
                <span className="hidden md:inline-block rounded-full py-[18px] px-8 text-pureWhite bg-trueBlack/50 backdrop-blur-[10px] opacity-0 group-hover/poc:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <Overline as="span" size="md" weight="500" className="flex justify-center items-center gap-3">
                        <TextWithRegisteredMark text={cta.label} />
                        <Caret width={16} height={16} />
                    </Overline>
                </span>
            </div>
            <div className="flex justify-center items-start mt-auto md:mt-0 relative">{logo}</div>
        </Link>
    )
};

export default ProductOverviewCard;
